import React, { useState, useMemo, useEffect } from 'react';
import { X, DollarSign, Sparkles, Loader2 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { safeAmount, formatCurrency, isWonStage } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { prequalificationService } from '../../services/api';
import MonthEventsModal from './MonthEventsModal';
import AIReportModal from './AIReportModal';

export default function RevenueDetailModal({ defaultRange, onClose }) {
  const { t } = useLanguage();
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showAIReport, setShowAIReport] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use the range passed from dashboard directly — no internal filter
  const dateRange = defaultRange || 'all';

  // Fetch aggregated analytics from the scalable API — no raw prequalifications needed
  useEffect(() => {
    setLoading(true);
    prequalificationService.getAnalytics({ dateRange })
      .then(data => {
        setAnalyticsData(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dateRange]);


  // Map analytics API data to stats shape used in the UI
  const stats = useMemo(() => {
    if (!analyticsData) return {
      totalRevenue: 0, potentialRevenue: 0, avgDealSize: 0,
      trendData: [], eventData: [], topDeals: [], eventsByMonth: {},
      total: 0, wonCount: 0
    };
    const a = analyticsData;
    const totalRevenue = a.revenueInPeriod || 0;
    const wonCount = a.bookedPeriodLeads || 0;
    const avgDealSize = wonCount > 0 ? totalRevenue / wonCount : 0;

    // Trend data comes directly from analytics API (monthly/weekly/etc breakdown)
    const trendData = (a.trendData || []).map(point => ({
      name: point.name,
      value: point.revenue || 0,
      rawDate: point.name
    }));

    // Use proper event type breakdown returned from API
    const eventData = Object.entries(a.revenueByEventType || {})
      .map(([name, data]) => ({ name, value: data.revenue || 0 }))
      .sort((a, b) => b.value - a.value);

    return {
      totalRevenue,
      potentialRevenue: a.pipelinePotential || 0,
      avgDealSize,
      trendData,
      eventData,
      topDeals: [],
      eventsByMonth: {},
      total: a.periodLeads || 0,
      wonCount
    };
  }, [analyticsData]);


  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {selectedMonth && (
        <MonthEventsModal
          monthName={selectedMonth}
          events={stats.eventsByMonth[selectedMonth] || []}
          onClose={() => setSelectedMonth(null)}
        />
      )}
      {showAIReport && <AIReportModal stats={stats} onClose={() => setShowAIReport(false)} />}

      <div className="bg-white w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in">
        <div className="bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-xl text-green-700">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('revenueAnalytics')}</h2>
              <p className="text-sm text-gray-500 flex items-center gap-2">
                {loading ? 'Loading data…' : `${stats.wonCount} won deals · ${stats.total} total prequalifications`}
                {!loading && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full capitalize">
                    {dateRange === 'all' ? 'All Time' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAIReport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-bold text-xs hover:bg-purple-200 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> {t('aiAnalysis')}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 py-20">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
              <p className="text-gray-500 font-medium">Loading revenue data…</p>
            </div>
          ) : (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                {t('grossRevenue')}
              </p>
              <h3 className="text-3xl font-bold text-gray-900">
                {formatCurrency(stats.totalRevenue)}
              </h3>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                {t('pipelinePotential')}
              </p>
              <h3 className="text-3xl font-bold text-gray-900">
                {formatCurrency(stats.potentialRevenue)}
              </h3>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm h-72">
              <h3 className="font-bold text-gray-900 mb-4">{t('revenueTrend')}</h3>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.trendData}>
                  <XAxis dataKey="name" />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#10B981"
                    fill="#10B981"
                    fillOpacity={0.1}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm h-72">
              <h3 className="font-bold text-gray-900 mb-4">{t('byEventType')}</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.eventData}>
                  <XAxis dataKey="name" />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="value" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="font-bold text-gray-900">{t('topDeals')}</h3>
            </div>
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3">{t('client')}</th>
                  <th className="px-6 py-3 text-right">{t('amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {stats.topDeals.map((d, i) => (
                  <tr key={i}>
                    <td className="px-6 py-3">{d.clientName}</td>
                    <td className="px-6 py-3 text-right font-bold text-green-600">
                      {formatCurrency(d.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
